import { useQuery, useMutation } from "@tanstack/react-query";
import { useConnectorUI } from "./adapter.js";
// ─── Queries ──────────────────────────────────────────────────────────────────
// Strict SaaS-pool view — only the caller's own default-workspace
// connections (org_id = self AND end_org_id IS NULL). Used by workflow
// manifest pickers and the default Connections page so tenant-scoped
// rows never appear.
//
// Both the filter (scope=saasPool) and the grouping (groupBy=connector)
// run server-side; the hook is a pure passthrough.
export function useSaasPoolConnections() {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    return useQuery({
        queryKey: [...ui.keys.connections.list(orgId), "saas-pool-strict"],
        queryFn: () => ui.apiFetch(`${ui.apiBase}/v1/connections?scope=saasPool&groupBy=connector`),
        enabled: ui.connectionsEnabled,
    });
}
// Explicit-end-org variant: forces an endOrgId filter regardless of the preview
// tenant context. Used by the bindings wizard so the customer's own
// connections show up — not the SaaS platform's.
export function useConnectionsForEndOrg(endOrgId) {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    return useQuery({
        queryKey: [...ui.keys.connections.list(orgId), "end-org", endOrgId ?? null],
        queryFn: () => ui.apiFetch(`${ui.apiBase}/v1/connections?endOrgId=${encodeURIComponent(endOrgId)}`),
        enabled: ui.connectionsEnabled && !!endOrgId,
        select: (connections) => {
            const byConnector = {};
            for (const conn of connections) {
                if (!byConnector[conn.connectorId])
                    byConnector[conn.connectorId] = [];
                byConnector[conn.connectorId].push(conn);
            }
            return { connections, connectionsByConnector: byConnector };
        },
    });
}
export function useConnections(opts) {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    const previewTenantId = ui.previewTenantId;
    // Preview-tenant context: show that customer's own connections.
    // Otherwise the scope is the injected environment's default view —
    // saasPool (org_id = self AND end_org_id IN (NULL, self)) for the
    // dashboard, org_platform for the admin console. Tenant-scoped rows are
    // never surfaced from this hook outside the preview branch.
    const scopeParam = ui.connectionScope === "org_platform" ? "orgId=org_platform" : "scope=saasPool";
    const url = previewTenantId
        ? `${ui.apiBase}/v1/connections?endOrgId=${encodeURIComponent(previewTenantId)}`
        : `${ui.apiBase}/v1/connections?${scopeParam}`;
    return useQuery({
        queryKey: [...ui.keys.connections.list(orgId), previewTenantId],
        queryFn: () => ui.apiFetch(url),
        enabled: opts?.enabled ?? ui.connectionsEnabled,
        select: (connections) => {
            const byConnector = {};
            for (const conn of connections) {
                if (!byConnector[conn.connectorId]) {
                    byConnector[conn.connectorId] = [];
                }
                byConnector[conn.connectorId].push(conn);
            }
            return { connections, connectionsByConnector: byConnector };
        },
    });
}
export function useConnectionsList(params = {}) {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    const previewTenantId = ui.previewTenantId;
    const { status, limit, offset, scope = "saasPool", endOrgId } = params;
    const qs = new URLSearchParams();
    if (limit !== undefined)
        qs.set("limit", String(limit));
    if (offset !== undefined)
        qs.set("offset", String(offset));
    if (status && status !== "all")
        qs.set("status", status);
    // Preview tenant: show that customer's own rows. Otherwise the injected
    // environment's default scope — saasPool for the dashboard, org_platform
    // for the admin console. Tenant rows are never surfaced outside preview,
    // EXCEPT through the caller's explicit `scope: "customers"`, which the API
    // gates on an account-tier, non-tenant-scoped session.
    if (previewTenantId) {
        qs.set("endOrgId", previewTenantId);
    }
    else if (ui.connectionScope === "org_platform") {
        qs.set("orgId", "org_platform");
    }
    else {
        qs.set("scope", scope);
        // Sent ONLY alongside the customers scope. On `saasPool` the API's branch
        // order makes the pool predicate win and the param a silent no-op, and a
        // parameter that is read on one scope and ignored on the other is the kind
        // of thing a caller reasonably assumes works.
        if (scope === "customers" && endOrgId)
            qs.set("endOrgId", endOrgId);
    }
    return useQuery({
        queryKey: [
            ...ui.keys.connections.list(orgId),
            "saas-pool",
            previewTenantId ?? null,
            // In the key, not just the URL: the two scopes are different row sets at
            // the same (status, limit, offset), so sharing a cache slot would show
            // the customers view the own-pool page it was holding.
            scope,
            endOrgId ?? null,
            status ?? "all",
            limit ?? null,
            offset ?? null,
        ],
        queryFn: () => ui.apiFetchRaw(`${ui.apiBase}/v1/connections?${qs.toString()}`),
        enabled: ui.connectionsEnabled,
        // Hold the page on screen while the next one loads. Every page is its own
        // cache slot, so without this Next starts an entry with no data, the table
        // unmounts for a spinner, and the pager below it slides up the page for the
        // length of the request and drops back when the rows land.
        placeholderData: (prev) => prev,
        select: (response) => ({
            connections: response.data ?? [],
            total: response.pagination?.total ?? (response.data ?? []).length,
        }),
    });
}
// ─── Mutations ────────────────────────────────────────────────────────────────
export function useDeleteConnection() {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    return useMutation({
        mutationFn: (id) => ui.apiFetch(`${ui.apiBase}/v1/connections/${id}`, ui.del),
        onSuccess: (_result, id) => {
            // Drop the row from every cached list FIRST, then invalidate.
            //
            // Invalidation on its own makes the screen depend on the refetch it
            // schedules, and React Query keeps the last good data when a refetch
            // fails — so a 5xx, a lapsed token, or a gateway restart between the
            // DELETE and the GET leaves the account the user just deleted sitting in
            // the picker until a full page reload, with nothing on screen saying so.
            // The server has already confirmed the delete by the time this runs, so
            // the cache can say so without waiting to be told again.
            ui.queryClient.setQueriesData({ queryKey: ui.keys.connections.all(orgId) }, (prev) => withoutConnection(prev, id));
            ui.queryClient.invalidateQueries({ queryKey: ui.keys.connections.all(orgId) });
        },
    });
}
/**
 * Apply `fn` to the connection rows inside whatever shape a cached connections
 * query holds: a bare list (useConnections / useConnectionsForEndOrg — `select`
 * groups it, the cache holds the array), a connector-keyed map
 * (useSaasPoolConnections, grouped server-side), or a paginated envelope
 * (useConnectionsList).
 *
 * `fn` returns null when it changed nothing, and the walk then returns the SAME
 * reference, so an unrelated cache entry does not re-render every list on
 * screen. `patchEnvelope` is for the extra bookkeeping only some edits need —
 * a removal has to fix `pagination.total`, a rename must not touch it.
 *
 * One walk for both callers: they were two near-identical copies of this
 * three-shape traversal, and a shape added to one would have been missed by
 * the other.
 */
function transformConnectionRows(data, fn, patchEnvelope) {
    if (Array.isArray(data)) {
        const rows = fn(data);
        return rows ? rows : data;
    }
    if (!data || typeof data !== "object")
        return data;
    const obj = data;
    if (Array.isArray(obj.data)) {
        const before = obj.data;
        const rows = fn(before);
        if (!rows)
            return data;
        const next = { ...obj, data: rows };
        return (patchEnvelope ? patchEnvelope(next, before, rows) : next);
    }
    let changed = false;
    const out = {};
    for (const [key, value] of Object.entries(obj)) {
        if (!Array.isArray(value)) {
            out[key] = value;
            continue;
        }
        const rows = fn(value);
        if (rows)
            changed = true;
        out[key] = rows ?? value;
    }
    return changed ? out : data;
}
/** Remove one connection from every cached shape. */
export function withoutConnection(data, id) {
    return transformConnectionRows(data, (rows) => {
        const kept = rows.filter((c) => c?.id !== id);
        return kept.length === rows.length ? null : kept;
    }, (obj) => {
        const pagination = obj.pagination;
        // The pager reads this, not the row count: leaving it stale makes a list
        // that has just lost its last row still claim a next page.
        return typeof pagination?.total === "number"
            ? { ...obj, pagination: { ...pagination, total: Math.max(0, pagination.total - 1) } }
            : obj;
    });
}
/** Set one connection's name in every cached shape. */
export function withRenamedConnection(data, id, name) {
    return transformConnectionRows(data, (rows) => {
        let changed = false;
        const out = rows.map((c) => {
            if (c?.id !== id || c?.name === name)
                return c;
            changed = true;
            return { ...c, name };
        });
        return changed ? out : null;
    });
}
/** Rename a connection (its account label). PUT /connections/:id { name }. */
export function useUpdateConnection() {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    return useMutation({
        mutationFn: ({ id, name }) => ui.apiFetch(`${ui.apiBase}/v1/connections/${id}`, ui.put({ name })),
        onSuccess: (result, { id, name }) => {
            // Write the new name into every cached list FIRST, then invalidate - the
            // same argument useDeleteConnection makes above, and it bites harder
            // here. React Query keeps the last good data when a refetch fails, so a
            // 5xx or a lapsed token between the PUT and the GET leaves the OLD name
            // on screen after a rename the server accepted: no error to show, and
            // nothing to tell the user it worked. Renaming is also the one mutation
            // where the person is watching that exact string for a change.
            ui.queryClient.setQueriesData({ queryKey: ui.keys.connections.all(orgId) }, 
            // The server's own value, falling back to what was sent. Neither the
            // UCL nor the Postgres PUT path trims today - the client does, before
            // sending - so these agree; preferring the response is what keeps them
            // agreeing if the server ever starts normalising.
            (prev) => withRenamedConnection(prev, id, result?.name ?? name));
            ui.queryClient.invalidateQueries({ queryKey: ui.keys.connections.all(orgId) });
        },
    });
}
/**
 * Make a connection its connector's default. PUT /connections/:id { isDefault: true }.
 *
 * The default is what the platform runs when a caller names no connection: workflow manifests
 * auto-wire to it, and the connector execute path uses it instead of refusing to choose between
 * several. Setting one always un-sets whichever held it before, so this is a move, not a toggle -
 * hence no `false` here.
 */
export function useSetDefaultConnection() {
    const ui = useConnectorUI();
    const orgId = ui.userContext?.orgId;
    return useMutation({
        mutationFn: (id) => ui.apiFetch(`${ui.apiBase}/v1/connections/${id}`, ui.put({ isDefault: true })),
        onSuccess: () => {
            ui.queryClient.invalidateQueries({ queryKey: ui.keys.connections.all(orgId) });
        },
    });
}
export function invalidateConnections(orgId, client) {
    // Query keys are inlined (not read from the adapter) because a free function
    // has no context; these mirror keys.connections.all(orgId) and the
    // widget-insights key that shows connection counts.
    return Promise.all([
        client.invalidateQueries({ queryKey: ["connections", orgId] }),
        client.invalidateQueries({ queryKey: ["widgets", orgId, "insights"] }),
    ]);
}
// ─── Picker credentials ───────────────────────────────────────────────────────
/** Mint a usable OAuth access token for a connection. The endpoint returns
 *  only the token (never the refresh token or the credentials blob) and
 *  refreshes on demand server-side. Used by the config page's file pickers. */
export async function fetchConnectionAccessToken(http, connectionId) {
    const data = await http.apiFetch(`${http.apiBase}/v1/connections/${connectionId}/access-token`, { method: "POST" });
    return data.access_token;
}
/** Best-effort read of a connection's auth-method authConfig — the home of
 *  per-connector picker keys (developerKey for Google, clientId for OneDrive),
 *  mirroring v1's authMethod.details. Returns {} when unavailable so callers
 *  fall back to the built-in default keys. */
export async function fetchConnectionAuthConfig(http, connectionId) {
    try {
        const meta = await http.apiFetch(`${http.apiBase}/v1/connections/${connectionId}`);
        if (!meta.authMethodId)
            return {};
        const method = await http.apiFetch(`${http.apiBase}/v1/auth-methods/${meta.authMethodId}`);
        return method.authConfig ?? {};
    }
    catch {
        return {};
    }
}
//# sourceMappingURL=use-connections.js.map